import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import Transfer from "@/screens/Developer/ProjectTransfer";
import DeveloperStateDisplay from "@components/Displays/DeveloperStateDisplay";
import { DeveloperLayout } from "@components/Layouts";
import { BackendPromise, url_reverse } from "@components/Utils";
import Accounts from "@screens/Developer/Accounts";
import AppForm from "@screens/Developer/AppForm";
import ContactInfo from "@screens/Developer/ContactInfo";
import CounterpartyDocs from "@screens/Developer/CounterpartyDocs";
import ModeratorComments from "@screens/Developer/ModeratorComments";
import MyTracker from "@screens/Developer/MyTracker";
import Orders from "@screens/Developer/Orders";
import PaymentInfo from "@screens/Developer/PaymentInfo";
import PublishersGamedevs from "@screens/Developer/PublishersGamedevs";
import SendLetter from "@screens/Developer/SendLetter";
import Act from "@screens/Developer/Statistics/Act";
import Documents from "@screens/Developer/Statistics/Documents";
import DocumentsReport from "@screens/Developer/Statistics/DocumentsReport";
import StatisticsOrders from "@screens/Developer/Statistics/Orders";
import StatisticsPayments from "@screens/Developer/Statistics/Payments";
import VkplayDmr from "@screens/Developer/Statistics/VkplayDmr";
import TechnicalInfo from "@screens/Developer/TechnicalInfo";
import NotFound from "@screens/NotFound";

export default function DeveloperScreen({ screen }) {
    const params = useParams();
    const { developerId } = params;
    const developerScreen = screen;
    const navigate = useNavigate();
    const commonInfo = useRouteLoaderData("commonInfo");
    const developerCommonInfo = useRouteLoaderData("developerCommonInfo");
    const [redirectUrl, setRedirectUrl] = useState(null);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl, { replace: true });
        }
    }, [redirectUrl]);

    const header = (
        <DeveloperHeaderTitle
            developer={developerCommonInfo.developer}
            showGamesLink={commonInfo.account?.is_super || commonInfo.account?.pr_moderator}
        />
    );

    const headerTools = <DeveloperStateDisplay developer={developerCommonInfo.developer} />;

    let active = "";
    let body = "";
    const props = { developerId, header, headerTools, screen };
    switch (developerScreen) {
        case "reroute": {
            const redirect_url = reroute(developerCommonInfo);
            if (redirect_url && redirect_url !== redirectUrl) {
                setRedirectUrl(redirect_url);
            }
            break;
        }
        case "app_form":
            active = "app_form";
            body = <AppForm {...props} />;
            break;
        case "accounts":
            active = "accounts";
            body = <Accounts {...props} />;
            break;
        case "contact_info":
            active = "contact_info";
            body = <ContactInfo {...props} />;
            break;
        case "payment_info":
            active = "payment_info";
            body = <PaymentInfo {...props} />;
            break;
        case "technical_info":
            active = "technical_info";
            body = <TechnicalInfo {...props} />;
            break;
        case "orders":
            active = "orders";
            body = <Orders {...props} />;
            break;
        case "statistics":
            active = `statistics.${params.kind}`;
            if (params.kind.endsWith("orders")) {
                body = <StatisticsOrders {...props} />;
            } else if (params.kind.endsWith("payments")) {
                body = <StatisticsPayments {...props} />;
            } else if (params.kind === "vkplay_dmr") {
                body = <VkplayDmr {...props} />;
            } else if (params.kind.endsWith("act")) {
                body = <Act {...props} />;
            } else if (params.kind.endsWith("documents")) {
                body = <Documents {...props} />;
            } else if (params.kind.endsWith("documents_report")) {
                body = <DocumentsReport {...props} />;
            }
            break;
        case "send_letter":
            active = "send_letter";
            body = <SendLetter {...props} />;
            break;
        case "mytracker":
            active = "mytracker";
            body = <MyTracker {...props} />;
            break;
        case "moderator_comments":
            active = "moderator_comments";
            body = <ModeratorComments {...props} />;
            break;
        case "counterparty_docs":
            active = "counterparty_docs";
            body = <CounterpartyDocs {...props} />;
            break;
        case "publishers":
        case "gamedevs":
            active = screen;
            body = <PublishersGamedevs {...props} />;
            break;
        case "projects_transfer":
            active = "projects_transfer";
            body = <Transfer {...props} />;
            break;
        default:
            break;
    }

    if (!body) {
        return <NotFound />;
    }

    return <DeveloperLayout body={body} active={active} />;
}

function DeveloperHeaderTitle({ developer, showGamesLink = false }) {
    const { t } = useTranslation();
    return developer ? (
        <>
            {developer.name}
            {showGamesLink && (
                <span>
                    {" "}
                    –{" "}
                    <Link to={url_reverse("games:list", null, { "filter-developer": developer.id })}>
                        {t("Список игр")}
                    </Link>
                </span>
            )}
        </>
    ) : null;
}

function reroute(developerCommonInfo) {
    const first_menu = Object.values(developerCommonInfo.developer_menu_availability)[0];
    if (!first_menu) {
        return;
    }
    if ("submenu" in first_menu) {
        return first_menu.submenu[0].url;
    }
    return first_menu.url;
}

export const developerCommonInfoLoader = async ({ params }) => {
    return await BackendPromise.get(url_reverse("api:developer:common_info", { developer_id: params.developerId }));
};
